import React, { useCallback, useEffect } from "react";
import { toastError } from "../../utils";
import { useNavigate } from "react-router";
import ItemAutoSuggest from "../sale/components/ItemAutoSuggest";
import AsyncSearchSelect from "../sale/components/AsyncSearchSelect";

const calculateTaxInclusivePrice = (price, gstRate, type) => {
  const p = parseFloat(price) || 0;
  const rate = parseFloat(gstRate) || 0;
  if (type === "inclusive" && p > 0 && rate > 0) return p;
  if (type === "exclusive" && p > 0 && rate > 0) return p * (1 + rate / 100);
  return p;
};

const ItemsSection = ({
  formData,
  setFormData,
  allItems,
  categories,
  calculateTaxInclusivePrice,
  partyId,
  onNewItemAdded,
  stockData,
  units,
  loadProductOptions,
  savedExpenses,
}) => {
  const navigate = useNavigate();
  const gstRates = [0, 5, 12, 18, 28];

  const recalculateTotals = (items, expenses) => {
    const itemsTotal = items.reduce((sum, item) => {
      const price = Number(item.purchasePrice) || 0;
      const quantity = Number(item.quantity) || 0;
      const gstRate = Number(item.gstRate) || 0;

      const taxType = item.purchasePriceType || "exclusive";

      let totalPrice = price;

      if (taxType === "exclusive") {
        totalPrice = price + (price * gstRate) / 100;
      }

      return sum + quantity * totalPrice;
    }, 0);

    // const itemsTotal = items.reduce((sum, item) => {
    //   const priceWithTax = calculateTaxInclusivePrice(
    //     item.purchasePrice ?? 0,
    //     item.gstRate ?? 0,
    //     item.purchasePriceType ?? "exclusive",
    //   );
    //   return sum + (item.quantity ?? 0) * priceWithTax;
    // }, 0);

    const expensesTotal = expenses.reduce((sum, exp) => {
      const amount = parseFloat(exp.price) || 0;
      const rate = parseFloat(exp.gstRate) || 0;
      const inclusive = exp.taxType === "inclusive";
      return sum + (inclusive ? amount : amount * (1 + rate / 100));
    }, 0);

    const totalAmount = itemsTotal + expensesTotal;
    const dueAmount = totalAmount - (parseFloat(formData.paidAmount) || 0);

    setFormData((prev) => ({
      ...prev,
      items,
      otherExpenses: expenses,
      totalAmount,
      dueAmount,
    }));
  };
  const removeItem = (index) => {
    const updated = formData.items.filter((_, i) => i !== index);
    recalculateTotals(updated, formData.otherExpenses);
  };

  const removeExpense = (index) => {
    const updated = formData.otherExpenses.filter((_, i) => i !== index);
    recalculateTotals(formData.items, updated);
  };

  const updateItemField = (index, field, value) => {
    const updatedItems = [...formData.items];
    const item = { ...updatedItems[index] };

    if (field === "name" && !value.trim()) {
      toastError("Item name cannot be empty");
      return;
    }

    // ---- DECIMAL FIELDS ----
    if (["purchasePrice", "salePrice", "gstRate"].includes(field)) {
      if (!/^\d*\.?\d*$/.test(value)) return;

      item[field] = value;
    }

    // ---- INTEGER ONLY FIELDS ----
    else if (["quantity", "minStockAlert"].includes(field)) {
      item[field] = value.replace(/[^0-9]/g, "");
    }

    // ---- NORMAL FIELDS ----
    else {
      item[field] = value;
    }

    updatedItems[index] = item;

    recalculateTotals(updatedItems, formData.otherExpenses);
  };

  const updateExpenseField = (index, field, value, validate = false) => {
    const updated = [...formData.otherExpenses];
    const exp = { ...updated[index] };

    // Basic updates
    exp[field] =
      field === "price" || field === "gstRate"
        ? value === ""
          ? 0
          : parseFloat(value) || 0
        : value;

    // ------ GST & TOTAL CALCULATION ------
    const amount = parseFloat(exp.price) || 0;
    const rate = parseFloat(exp.gstRate) || 0;
    const inclusive = exp.taxType === "inclusive";

    let gst = 0;
    let total = amount;

    if (inclusive && amount > 0 && rate > 0) {
      gst = amount - amount / (1 + rate / 100);
    } else if (!inclusive && amount > 0 && rate > 0) {
      gst = (amount * rate) / 100;
      total = amount + gst;
    }

    // Save GST & total INTO STATE
    exp.gst = gst.toFixed(2);
    exp.manualTotal = total.toFixed(2);

    updated[index] = exp;

    recalculateTotals(formData.items, updated);
  };

  const handleSelectItem = (index, selected) => {
    if (!selected?._id) return;

    const latestPrice =
      selected.latestPurchasePrice ?? selected.batches?.[0]?.purchasePrice ?? 0;

    const updated = [...formData.items];

    updated[index] = {
      ...updated[index],
      productId: selected._id,
      name: selected.name,
      unit: selected.unit,
      category: selected.category?.name,
      purchasePrice: latestPrice,
      purchasePriceType: selected.taxType || "exclusive",
      gstRate: selected.gstRate || 0,
      expiryDate: updated[index].expiryDate || "",
    };

    recalculateTotals(updated, formData.otherExpenses);
  };

  useEffect(() => {
    if (formData.otherExpenses.length === 0) {
      setFormData((prev) => ({
        ...prev,
        otherExpenses: [
          {
            name: "",
            price: "",
            taxType: "exclusive",
            gstRate: "",
            gst: "",
            manualTotal: "",
          },
        ],
      }));
    }
  }, [formData.otherExpenses.length]);

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0 text-dark">Items & Product</h5>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate("/items/add")}
        >
          Add New Item
        </button>
      </div>

      <div className="card-body">
        <div className="mb-4">
          <div className="table-responsive border rounded">
            <table
              className="table table-bordered align-middle mb-0"
              style={{
                minWidth: "100%",
                width: "max-content",
                tableLayout: "fixed",
              }}
            >
              <thead
                className="table-light"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#f8f9fa",
                }}
              >
                <tr>
                  <th style={{ width: "25%", minWidth: "200px" }}>
                    Item/Product
                  </th>
                  <th style={{ width: "8%", minWidth: "80px" }}>Qty</th>
                  <th style={{ width: "12%", minWidth: "120px" }}>Unit</th>
                  <th style={{ width: "18%", minWidth: "180px" }}>
                    Purchase Price / Unit
                  </th>
                  <th style={{ width: "18%", minWidth: "180px" }}>
                    Total Price
                  </th>
                  <th style={{ width: "12%", minWidth: "90px" }}>Tax Type</th>
                  <th
                    style={{ width: "10%", minWidth: "100px" }}
                    className="text-nowrap"
                  >
                    GST Rate (%)
                  </th>
                  <th
                    style={{ width: "12%", minWidth: "120px" }}
                    className="text-nowrap"
                  >
                    Total GST (₹)
                  </th>
                  {/* <th style={{ width: "15%", minWidth: "150px" }}>Category</th> */}
                  <th style={{ minWidth: "150px" }}>Expiry Date</th>

                  <th style={{ width: "12%", minWidth: "120px" }}>Total</th>
                  <th style={{ width: "10%", minWidth: "80px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(formData.items.length === 0 ? [{}] : formData.items).map(
                  (item = {}, index) => {
                    const isEmptyRow = formData.items.length === 0;

                    const price = Number(item.purchasePrice) || 0;
                    const qty = Number(item.quantity) || 0;
                    const gstRate = parseFloat(item.gstRate) || 0;
                    const taxType = item.purchasePriceType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let baseAmount = qty * price;
                    let gstAmount = 0;

                    if (gstRate > 0) {
                      if (isInclusive) {
                        gstAmount = (baseAmount * gstRate) / (100 + gstRate);
                      } else {
                        gstAmount = (baseAmount * gstRate) / 100;
                      }
                    }

                    const autoTotal = isInclusive
                      ? baseAmount
                      : baseAmount + gstAmount;

                    return (
                      <tr key={isEmptyRow ? "empty-item" : index}>
                        <td
                          style={{
                            width: "25%",
                            minWidth: "200px",
                            zIndex: "9999",
                          }}
                        >
                          <AsyncSearchSelect
                            value={
                              item.productId
                                ? {
                                    label: item.name || "Selected Item",
                                    value: item.productId,
                                  }
                                : null
                            }
                            loadOptions={loadProductOptions}
                            onChange={(selected) => {
                              const updatedItems = [...formData.items];

                              if (!selected) {
                                updatedItems[index] = {
                                  productId: "",
                                  name: "",
                                  purchasePrice: 0,
                                  quantity: 0,
                                  unit: "",
                                  category: "",
                                  purchasePriceType: "exclusive",
                                  gstRate: 0,
                                  expiryDate: "",
                                };

                                setFormData((prev) => ({
                                  ...prev,
                                  items: updatedItems,
                                }));

                                recalculateTotals(
                                  updatedItems,
                                  formData.otherExpenses,
                                );
                                return;
                              }

                              const product = selected.raw;
                              if (!product) return;

                              updatedItems[index] = {
                                ...updatedItems[index],
                                productId: product._id,
                                name: product.name,

                                purchasePrice: product.latestPurchasePrice || 0,
                                // quantity: product.stock || 0,
                                unit: product.unit || "",
                                expiryDate: product.expiryDate || "",
                                category: product.category || "",
                                purchasePriceType:
                                  product.taxType || "exclusive",
                                gstRate: product.gstRate || 0,
                              };

                              setFormData((prev) => ({
                                ...prev,
                                items: updatedItems,
                              }));

                              recalculateTotals(
                                updatedItems,
                                formData.otherExpenses,
                              );
                            }}
                            placeholder="Search product..."
                            minInputLength={2}
                            debounceWait={400}
                            isClearable
                          />
                        </td>

                        <td style={{ width: "8%", minWidth: "80px" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={item.quantity ?? ""}
                            onChange={(e) =>
                              updateItemField(index, "quantity", e.target.value)
                            }
                            disabled={item.type === "service"}
                            style={{ width: "100%" }}
                          />
                        </td>

                        <td style={{ width: "12%", minWidth: "120px" }}>
                          <select
                            className="form-select form-select-sm"
                            value={item.unit ?? ""}
                            onChange={(e) =>
                              updateItemField(index, "unit", e.target.value)
                            }
                            disabled={item.type === "service"}
                            style={{ width: "100%" }}
                          >
                            <option value="" disabled>
                              Select Unit
                            </option>
                            {units?.length === 0 ? (
                              <option value="" disabled>
                                No Units
                              </option>
                            ) : (
                              <>
                                {units?.map((u) => (
                                  <option value={u.name} key={u._id}>
                                    {u.name}
                                  </option>
                                ))}
                              </>
                            )}
                          </select>
                        </td>

                        <td style={{ width: "18%", minWidth: "180px" }}>
                          <div className="d-flex" style={{ gap: "5px" }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="form-control form-control-sm flex-grow-1"
                              value={item.purchasePrice ?? ""}
                              onChange={(e) =>
                                updateItemField(
                                  index,
                                  "purchasePrice",
                                  e.target.value,
                                )
                              }
                            />
                          </div>
                        </td>

                        <td style={{ width: "18%", minWidth: "180px" }}>
                          <div className="d-flex" style={{ gap: "5px" }}>
                            <input
                              type="text"
                              className="form-control form-control-sm flex-grow-1"
                              value={baseAmount}
                              readOnly
                            />
                          </div>
                        </td>

                        <td style={{ width: "10%", minWidth: "100px" }}>
                          <select
                            className="form-select form-select-sm"
                            value={item.purchasePriceType || "exclusive"}
                            onChange={(e) =>
                              updateItemField(
                                index,
                                "purchasePriceType",
                                e.target.value,
                              )
                            }
                            style={{ width: "100%" }}
                          >
                            <option value="exclusive">Exclusive</option>
                            <option value="inclusive">Inclusive</option>
                          </select>
                        </td>

                        <td style={{ width: "10%", minWidth: "100px" }}>
                          <select
                            className="form-select form-select-sm"
                            value={item.gstRate ?? 0}
                            onChange={(e) =>
                              updateItemField(index, "gstRate", e.target.value)
                            }
                            style={{ width: "100%" }}
                          >
                            {gstRates.map((r) => (
                              <option key={r} value={r}>
                                {r}%
                              </option>
                            ))}
                          </select>
                        </td>

                        <td
                          style={{ width: "12%", minWidth: "120px" }}
                          className="text-end"
                        >
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={
                              gstAmount > 0
                                ? `₹ ${gstAmount.toFixed(2)}`
                                : "₹ 0.00"
                            }
                            readOnly
                          />
                        </td>

                        {/* <td style={{ width: "15%", minWidth: "150px" }}>
                          <select
                            className="form-select form-select-sm"
                            value={item.category || ""}
                            onChange={(e) =>
                              updateItemField(index, "category", e.target.value)
                            }
                            style={{ width: "100%" }}
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat._id} value={cat._id}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        </td> */}
                        <td style={{ width: "12%", minWidth: "150px" }}>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={item.expiryDate || ""}
                            onChange={(e) =>
                              updateItemField(
                                index,
                                "expiryDate",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td style={{ width: "12%", minWidth: "120px" }}>
                          <input
                            type="text"
                            className="form-control form-control-sm fw-bold"
                            value={item.manualTotal ?? ""}
                            placeholder={autoTotal.toFixed(2)}
                            step="0.01"
                            onChange={(e) =>
                              updateItemField(
                                index,
                                "manualTotal",
                                e.target.value === ""
                                  ? null
                                  : parseFloat(e.target.value),
                              )
                            }
                            style={{ width: "100%" }}
                            readOnly
                          />
                        </td>

                        <td
                          style={{ width: "10%", minWidth: "80px" }}
                          className="text-center"
                        >
                          {formData.items.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeItem(index)}
                              style={{ minWidth: "40px" }}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  },
                )}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                items: [
                  ...prev.items,
                  {
                    productId: "",
                    name: "",
                    quantity: "",
                    unit: "pcs",
                    purchasePrice: "",
                    purchasePriceType: "exclusive",
                    gstRate: 0,
                    expiryDate: "",
                    manualTotal: null,
                  },
                ],
              }))
            }
          >
            + Add Item Row
          </button>
        </div>

        <hr className="my-4" />

        {/* Other Expenses Table */}
        <div className="mb-4">
          <h5 className="mb-4 text-dark">Expenses</h5>
          <div
            className="table-responsive border rounded"
            style={{
              maxHeight: "300px",
              overflow: "auto",
              position: "relative",
            }}
          >
            <table
              className="table table-bordered align-middle mb-0"
              style={{
                minWidth: "100%",
                width: "max-content",
                tableLayout: "fixed",
              }}
            >
              <thead
                className="table-light"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  backgroundColor: "#f8f9fa",
                }}
              >
                <tr>
                  <th style={{ width: "25%", minWidth: "200px" }}>
                    Expense Name
                  </th>
                  <th style={{ width: "15%", minWidth: "120px" }}>
                    Amount (₹)
                  </th>
                  <th style={{ width: "15%", minWidth: "120px" }}>Tax Type</th>
                  <th style={{ width: "15%", minWidth: "120px" }}>GST %</th>
                  <th style={{ width: "15%", minWidth: "120px" }}>GST (₹)</th>
                  <th style={{ width: "15%", minWidth: "120px" }}>Total (₹)</th>
                  <th style={{ width: "10%", minWidth: "80px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(formData.otherExpenses.length === 0
                  ? [{}]
                  : formData.otherExpenses
                ).map((expense = {}, index) => {
                  const isEmptyRow = formData.otherExpenses.length === 0;

                  const amount = parseFloat(expense.price) || 0;
                  const rate = parseFloat(expense.gstRate) || 0;
                  const inclusive = expense.taxType === "inclusive";

                  let gst = 0;
                  let total = amount;
                  if (inclusive && amount > 0 && rate > 0) {
                    gst = amount - amount / (1 + rate / 100);
                  } else if (!inclusive && amount > 0 && rate > 0) {
                    gst = (amount * rate) / 100;
                    total = amount + gst;
                  }

                  return (
                    <tr key={isEmptyRow ? "empty-exp" : index}>
                      <td style={{ width: "25%", minWidth: "200px" }}>
                        {/* <input
                          type="text"
                          className="form-control form-control-sm"
                          value={expense.name || ""}
                          onChange={(e) =>
                            updateExpenseField(
                              index,
                              "name",
                              e.target.value,
                              false,
                            )
                          }
                          onBlur={(e) =>
                            updateExpenseField(
                              index,
                              "name",
                              e.target.value,
                              true,
                            )
                          }
                          placeholder="Enter expense name"
                          style={{ width: "100%" }}
                        /> */}

                        <select
                          className="form-select form-select-sm"
                          value={expense.expenseId || ""}
                          onChange={(e) => {
                            const selectedExpense = (savedExpenses || []).find(
                              (item) => item._id === e.target.value,
                            );

                            if (!selectedExpense) return;

                            const updatedExpenses = [...formData.otherExpenses];
                            const currentExpense = {
                              ...updatedExpenses[index],
                            };

                            currentExpense.expenseId = selectedExpense._id;
                            currentExpense.name = selectedExpense.title;
                            currentExpense.price =
                              Number(selectedExpense.amount) || 0;

                            const amount = currentExpense.price;
                            const rate = Number(currentExpense.gstRate) || 0;
                            const inclusive =
                              currentExpense.taxType === "inclusive";

                            let gst = 0;
                            let total = amount;

                            if (amount > 0 && rate > 0) {
                              if (inclusive) {
                                gst = amount - amount / (1 + rate / 100);
                              } else {
                                gst = (amount * rate) / 100;
                                total = amount + gst;
                              }
                            }

                            currentExpense.gst = gst.toFixed(2);
                            currentExpense.manualTotal = total.toFixed(2);

                            updatedExpenses[index] = currentExpense;

                            recalculateTotals(formData.items, updatedExpenses);
                          }}
                          style={{ width: "100%" }}
                        >
                          <option value="">Select Expense</option>

                          {!savedExpenses || savedExpenses.length === 0 ? (
                            <option value="" disabled>
                              No Expenses
                            </option>
                          ) : (
                            savedExpenses.map((item) => (
                              <option key={item._id} value={item._id}>
                                {item.title}
                              </option>
                            ))
                          )}
                        </select>
                      </td>

                      <td style={{ width: "15%", minWidth: "120px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={expense.price || ""}
                          onChange={(e) =>
                            updateExpenseField(index, "price", e.target.value)
                          }
                          placeholder="0.00"
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td style={{ width: "15%", minWidth: "120px" }}>
                        <select
                          className="form-select form-select-sm"
                          value={expense.taxType || "exclusive"}
                          onChange={(e) =>
                            updateExpenseField(index, "taxType", e.target.value)
                          }
                          style={{ width: "100%" }}
                        >
                          <option value="exclusive">Exclusive</option>
                          <option value="inclusive">Inclusive</option>
                        </select>
                      </td>

                      <td style={{ width: "15%", minWidth: "120px" }}>
                        <select
                          className="form-select form-select-sm"
                          value={expense.gstRate ?? ""}
                          onChange={(e) =>
                            updateExpenseField(index, "gstRate", e.target.value)
                          }
                          style={{ width: "100%" }}
                        >
                          {gstRates.map((r) => (
                            <option key={r} value={r}>
                              {r}%
                            </option>
                          ))}
                        </select>
                      </td>

                      <td style={{ width: "15%", minWidth: "120px" }}>
                        <input
                          type="text"
                          value={expense.gst || ""}
                          onChange={(e) =>
                            updateExpenseField(index, "gst", e.target.value)
                          }
                          className="form-control form-control-sm text-end"
                          placeholder="0.00"
                          style={{ width: "100%" }}
                          readOnly
                        />
                      </td>

                      <td style={{ width: "15%", minWidth: "120px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm text-end"
                          value={expense.manualTotal ?? ""}
                          placeholder={total.toFixed(2)}
                          onChange={(e) =>
                            updateExpenseField(
                              index,
                              "manualTotal",
                              e.target.value === ""
                                ? null
                                : parseFloat(e.target.value),
                            )
                          }
                          readOnly
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td
                        style={{ width: "10%", minWidth: "80px" }}
                        className="text-center"
                      >
                        {formData.otherExpenses.length > 0 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeExpense(index)}
                            style={{ minWidth: "40px" }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={() =>
              setFormData((prev) => ({
                ...prev,
                otherExpenses: [
                  ...prev.otherExpenses,
                  {
                    name: "",
                    price: "",
                    taxType: "exclusive",
                    gstRate: 0,

                    manualTotal: null,
                  },
                ],
              }))
            }
          >
            + Add Expense Row
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemsSection;
